import { useEffect, useState } from "react";
import { Loader2, Save, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

const inputCls = "w-full rounded-xl border border-inkline bg-parchment px-4 py-2.5 text-sm text-ink outline-none transition-colors focus:border-spice";

/** Google Business review link — shown on the review thank-you page and in review-request emails when set. */
export const GoogleReviewSetting = () => {
  const [url, setUrl] = useState("");
  const [autoPublish, setAutoPublish] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/public").then(({ data }) => { setUrl(data.google_review_url || ""); setAutoPublish(data.auto_publish_reviews !== false); }).catch(() => {});
  }, []);

  const save = async (e, nextAuto = autoPublish) => {
    e?.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings", { google_review_url: url.trim(), auto_publish_reviews: nextAuto });
      setUrl(data.google_review_url);
      setAutoPublish(data.auto_publish_reviews);
      toast.success(e ? (data.google_review_url ? "Google review link saved — customers now get an 'Also post on Google' button." : "Google review link cleared.")
        : data.auto_publish_reviews ? "4–5 star reviews now go live instantly." : "All new reviews now wait for your approval.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="mb-6 rounded-2xl border border-inkline bg-cream p-5" data-testid="google-review-setting">
      <div className="flex flex-wrap items-start justify-between gap-4 border-b border-inkline pb-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted2">Visitor reviews</div>
          <p className="mt-1 text-xs text-muted2">Anyone can post a review from the homepage. With auto-publish on, 4–5 star reviews appear instantly; 1–3 star reviews always wait for you. You can hide any review anytime.</p>
        </div>
        <button type="button" role="switch" aria-checked={autoPublish} disabled={saving} onClick={(ev) => { ev.preventDefault(); save(null, !autoPublish); }} data-testid="auto-publish-toggle"
          className={`inline-flex shrink-0 items-center gap-2 rounded-full px-3 py-1.5 font-mono text-[11px] font-semibold transition-colors ${autoPublish ? "bg-halal/15 text-halal" : "bg-ink/5 text-ink/70"}`}>
          <span className={`h-2.5 w-2.5 rounded-full ${autoPublish ? "bg-halal" : "bg-muted2"}`} /> Auto-publish {autoPublish ? "ON" : "OFF"}
        </button>
      </div>
      <label className="mt-4 block font-mono text-[10px] uppercase tracking-[0.25em] text-muted2" htmlFor="google-review-url">Google Business review link</label>
      <div className="mt-2 flex flex-col gap-2 sm:flex-row">
        <input id="google-review-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://g.page/r/.../review" className={inputCls} data-testid="google-review-url-input" />
        <button type="submit" disabled={saving} className="inline-flex items-center justify-center gap-2 rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-parchment transition-colors hover:bg-spice disabled:opacity-60" data-testid="google-review-save-btn">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
        </button>
      </div>
      <p className="mt-2 text-xs text-muted2">After a customer reviews you here, they see an "Also post on Google" button pointing to this link. It's also mentioned in the review-request email.</p>
    </form>
  );
};

const ago = (iso) => new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

/** Newsletter list + the Thursday weekly-menu campaign. */
export const SubscribersPanel = () => {
  const [data, setData] = useState(null);
  const [sending, setSending] = useState(false);

  const load = () => api.get("/admin/subscribers").then((r) => setData(r.data)).catch((err) => toast.error(formatApiError(err)));
  useEffect(() => { load(); }, []);

  const sendNow = async () => {
    if (!window.confirm(`Send this week's menu to ${data.active_count} subscriber(s) and all past customers now?`)) return;
    setSending(true);
    try {
      const { data: res } = await api.post("/admin/campaigns/weekly-menu/send");
      toast.success(`Weekly menu sent to ${res.sent} recipient(s).`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  if (!data) return <div className="rounded-2xl border border-inkline bg-cream p-8 text-center text-sm text-muted2">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="subscribers-panel">
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-inkline bg-cream p-5">
        <div>
          <div className="font-display text-2xl font-bold text-ink" data-testid="subscribers-active-count">{data.active_count} <span className="text-base font-medium text-muted2">active subscribers</span></div>
          <p className="mt-1 text-xs text-muted2">
            The weekly menu goes out automatically every <strong>Thursday at 10am ET</strong> to subscribers and everyone who has paid for an order.
            {data.last_campaign ? ` Last sent ${ago(data.last_campaign.created_at)} to ${data.last_campaign.sent_count} people (${data.last_campaign.trigger}).` : " Not sent yet."}
          </p>
        </div>
        <button onClick={sendNow} disabled={sending} className="inline-flex items-center gap-2 rounded-full bg-spice px-5 py-2.5 text-sm font-bold text-parchment transition-colors hover:bg-ink disabled:opacity-60" data-testid="weekly-menu-send-btn">
          {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send this week's menu now
        </button>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-inkline bg-cream">
        <table className="w-full min-w-[560px] text-left text-sm" data-testid="subscribers-table">
          <thead>
            <tr className="border-b border-inkline font-mono text-[10px] uppercase tracking-[0.2em] text-muted2">
              <th className="p-4">Email</th><th className="p-4">Source</th><th className="p-4">Joined</th><th className="p-4">Status</th>
            </tr>
          </thead>
          <tbody>
            {data.subscribers.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-muted2">No subscribers yet — the footer signup box and paid orders feed this list.</td></tr>}
            {data.subscribers.map((s) => (
              <tr key={s.email} className="border-b border-inkline/60 last:border-0" data-testid={`subscriber-row-${s.email.replace(/[^a-z0-9]/gi, "").slice(0, 12)}`}>
                <td className="p-4 text-ink/90">{s.email}</td>
                <td className="p-4 font-mono text-xs text-muted2">{s.source}</td>
                <td className="p-4 text-xs text-muted2">{new Date(s.created_at).toLocaleDateString()}</td>
                <td className="p-4">
                  {s.active
                    ? <span className="rounded-full bg-halal/10 px-2.5 py-1 font-mono text-[10px] font-bold text-halal">Active</span>
                    : <span className="rounded-full bg-ink/5 px-2.5 py-1 font-mono text-[10px] font-semibold text-muted2">Unsubscribed</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
